import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  LogOut,
  Menu,
  X,
  User,
  Plug,
  Home,
  Zap,
  Rocket,
  Target,
  BarChart2,
  Megaphone,
  CreditCard,
  HelpCircle,
  Lock,
  Settings,
  LayoutDashboard,
  Sparkles,
  CalendarRange,
  CalendarDays,
  FileText,
  Brain,
  Images,
} from 'lucide-react';
import './Sidebar.css';
import { AuthContext } from 'components/context/AuthContext';
import { useHelpCenter } from 'components/context/HelpCenterContext';
import { ModalsContext } from 'components/context/ModalsContext';
import { useGoogleAds } from '../hooks/useGoogleAds';
import { useMetaAds } from '../hooks/useMetaAds';
import { GoogleIcon, MetaIcon } from 'components';
import CompactConnectionBadge from './BrandSelector/CompactConnectionBadge';
import MobileBrandSelectorSheet from './BrandSelector/MobileBrandSelectorSheet';
import { useQueryClient } from '@tanstack/react-query';
import { logApi } from '../api';
import { invalidateObjetivosQueries, useObjetivosQuery } from '../queries/objetivos';
import { ChevronDown } from 'lucide-react';

const { createLog } = logApi;

const CHROME_LOCK_TOOLTIP = 'Completá el paso actual para continuar';

// 3×3 dot grid ("Más").
const NineDots = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
    {[3, 9, 15].flatMap((y) => [3, 9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
  </svg>
);

const ANALYTICS_SUBMENU = [
  { label: 'Dashboard', path: '/analytics20', description: 'Analytics' },
  { label: 'Reporting', path: '/reporting', description: 'Reporting' }
];

const ADS_SUBMENU = [
  { label: 'Edad de anuncios', path: '/ads-age', description: 'Meta' },
  { label: 'Performance anuncios', path: '/ads-performance', description: 'Performance' },
];

const Sidebar = ({ onExpandedChange, onNavigate, showMobileFooterMenu = true, onSidebarStateChange }) => {
  const location = useLocation();
  const defaultNavigate = useNavigate();
  const navigate = onNavigate || defaultNavigate;
  const { auth, logout, hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { openHelpCenter } = useHelpCenter();
  const { openPlansModal, openConnectionsModal, isMandatoryAdsObjectivesChromeLocked } =
    useContext(ModalsContext);
  const chromeLocked = Boolean(isMandatoryAdsObjectivesChromeLocked);
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const queryClient = useQueryClient();
  const activeBrandId = auth?.user?.active_brand_id;
  const activeBrand = auth?.user?.brands?.find((b) => b.id === activeBrandId);
  const googleConnected = activeBrand ? Boolean(activeBrand.google_ads_connected) : hasGoogleAdsConnected;
  const metaConnected = activeBrand ? Boolean(activeBrand.meta_connected) : hasMetaConnected;

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showOtras, setShowOtras] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: objectives = [], isPending: loadingObjectives } = useObjetivosQuery(activeBrandId);
  const [isBrandSelectorOpen, setIsBrandSelectorOpen] = useState(false);
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true
  });
  const [openSubmenuLabel, setOpenSubmenuLabel] = useState(null);
  const profileMenuRef = useRef(null);
  const sidebarRef = useRef(null);
  const userCampaignsVisibility = auth?.user?.show_campaigns_module;
  const activeBrandCampaignsVisibility = auth?.user?.active_brand?.show_campaigns_module;
  // Por defecto mostramos "Campañas" en el sidebar.
  // Solo lo ocultamos si el usuario o la marca lo desactivan explícitamente.
  const showCampaignsModule = userCampaignsVisibility !== false && activeBrandCampaignsVisibility !== false;

  useEffect(() => {
    const handler = () => {
      if (activeBrandId != null) invalidateObjetivosQueries(queryClient, activeBrandId);
    };
    window.addEventListener('objectives-created', handler);
    return () => window.removeEventListener('objectives-created', handler);
  }, [activeBrandId, queryClient]);

  const isOptimizerLocked = !loadingObjectives && Array.isArray(objectives) && objectives.length === 0;

  // Notificar al layout principal el ancho fijo del sidebar
  useEffect(() => {
    if (onSidebarStateChange) {
      onSidebarStateChange({
        isCompressed: true,
        // Admin usa el rail angosto estilo ClickUp; el resto, el sidebar ancho.
        width: auth?.user?.is_admin ? 76 : 240,
      });
    }
  }, [onSidebarStateChange, auth?.user?.is_admin]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const sz = {
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      };
      setScreenSize(sz);
      if (sz.isDesktop) onExpandedChange?.(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onExpandedChange]);

  useEffect(() => {
    if (screenSize.isMobile || screenSize.isTablet) {
      setShowProfileMenu(false);
    }
  }, [location.pathname, screenSize]);

  // Handle click outside profile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleNavigation = useCallback((path, disabled, label, event) => {
    if (disabled) return;
    
    // Detectar si se presionó Ctrl/Cmd o clic medio para abrir en nueva pestaña
    const isMiddleClick = event?.button === 1;
    const isCtrlClick = event?.ctrlKey || event?.metaKey;
    
    if (isMiddleClick || isCtrlClick) {
      event?.preventDefault();
      // Log the page visit with the menu item label
      createLog({
        level: 'info',
        message: `Click en ${label} en sidebar (nueva pestaña)`,
      });
      
      // Abrir en nueva pestaña usando window.open con URL completa
      const fullUrl = window.location.origin + path;
      const newWindow = window.open(fullUrl, '_blank');
      if (newWindow) {
        newWindow.opener = null; // Seguridad: evitar que la nueva ventana acceda a window.opener
      }
      return;
    }
    
    // Navegación normal
    event?.preventDefault();
    // Log the page visit with the menu item label
    createLog({
      level: 'info',
      message: `Click en ${label} en sidebar`,
    });
    
    navigate(path);
  }, [navigate]);

  const handleLogout = async () => {
    createLog({
      level: 'info',
      message: 'Click en "Cerrar sesión" en sidebar'
    });
    
    setShowProfileMenu(false);
    await logout();
    sessionStorage.removeItem('active_session_id');
    sessionStorage.removeItem('recent_sessions');
    navigate('/login');
  };

  const hasBrandPanel = Boolean(auth?.user?.has_brand_panel);
  const isAdmin = Boolean(auth?.user?.is_admin);
  // Growth partner: acceso restringido a solo el Panel (+ Perfil para invitar).
  const isGrowthPartner = Boolean(auth?.user?.is_growth_partner);
  // Admin ve "Panel" (todas las marcas); el cliente ve "Mi panel" (su marca).
  const panelItem = useMemo(
    () =>
      isAdmin
        ? { icon: LayoutDashboard, label: 'Panel', path: '/admin/panel' }
        : hasBrandPanel
          ? { icon: LayoutDashboard, label: 'Mi panel', path: '/mi-panel' }
          : null,
    [isAdmin, hasBrandPanel]
  );

  const menuItemsBase = useMemo(
    () => {
      if (isGrowthPartner) {
        return [
          { icon: LayoutDashboard, label: 'Panel', path: '/mi-panel' },
          { icon: Sparkles, label: 'Bebo IA', path: '/admin/bebo' },
          { icon: Settings, label: 'Mi configuración', path: '/profile' },
          { icon: User, label: 'Usuario', path: '/profile/users' },
        ];
      }
      const inicioItem = { icon: Home, label: 'Inicio', path: '/home', dataTour: 'nav-inicio' };
      const optimizacionesItem = { icon: Rocket, label: 'Optimizaciones', path: '/optimizador', dataTour: 'nav-optimizaciones' };
      const objetivosItem = { icon: Target, label: 'Objetivos', path: '/objetivos', dataTour: 'nav-objetivos' };
      const campanasItems = showCampaignsModule ? [{ icon: Zap, label: 'Campañas', path: '/campaigns', dataTour: 'nav-campañas' }] : [];
      const anunciosItem = { icon: Megaphone, label: 'Anuncios', path: '/ads-age', dataTour: 'nav-anuncios', submenu: ADS_SUBMENU };
      const analyticsItem = { icon: BarChart2, label: 'Analytics', path: '/analytics20', dataTour: 'nav-analytics', submenu: ANALYTICS_SUBMENU };

      // Admin: agrupado en secciones (Bebo IA / Dashboard / Otras herramientas).
      if (isAdmin) {
        const otras = [
          inicioItem,
          { icon: CalendarRange, label: 'Media planning', path: '/admin/media-planning' },
          { icon: CalendarDays, label: 'Calendario de promos', path: '/admin/promo-calendar' },
          optimizacionesItem,
          objetivosItem,
          ...campanasItems,
          anunciosItem,
          analyticsItem,
          { icon: Plug, label: 'Conexiones', onClick: () => openConnectionsModal?.() },
          { icon: HelpCircle, label: 'Soporte', onClick: () => openHelpCenter() },
          { icon: CreditCard, label: 'Suscripción', onClick: () => openPlansModal?.() },
          { icon: Settings, label: 'Configuración', path: '/profile' },
        ].map((it) => ({ ...it, group: 'otras' }));
        return [
          { section: 'Bebo IA' },
          { icon: Sparkles, label: 'Bebo IA', path: '/admin/bebo' },
          { icon: Images, label: 'Biblioteca', path: '/admin/creative-library' },
          { section: 'Dashboard' },
          ...(panelItem ? [panelItem] : []),
          { icon: FileText, label: 'Reportes', path: '/admin/reportes' },
          { section: 'Otras herramientas', collapsible: true },
          ...otras,
        ];
      }
      // No-admin (cliente): lista plana, sin cambios.
      return [
        inicioItem,
        ...(panelItem ? [panelItem] : []),
        optimizacionesItem,
        objetivosItem,
        ...campanasItems,
        anunciosItem,
        analyticsItem,
      ];
    },
    [
      showCampaignsModule,
      panelItem,
      isAdmin,
      isGrowthPartner,
      openConnectionsModal,
      openHelpCenter,
      openPlansModal,
    ]
  );

  const menuItemsWithLock = useMemo(
    () => menuItemsBase.map((item) => ({ ...item, disabled: Boolean(item.disabled) || chromeLocked })),
    [menuItemsBase, chromeLocked]
  );

  const profileItems = [
    {
      icon: LogOut,
      label: 'Cerrar sesión',
      onClick: handleLogout,
    },
  ];

  const mobileMenuBase = useMemo(
    () => {
      if (isGrowthPartner) {
        return [
          { icon: LayoutDashboard, label: 'Panel', path: '/mi-panel' },
          { icon: Sparkles, label: 'Bebo IA', path: '/admin/bebo' },
          { icon: Settings, label: 'Mi configuración', path: '/profile' },
          { icon: User, label: 'Usuario', path: '/profile/users' },
        ];
      }
      return [
        ...(isAdmin ? [{ icon: Sparkles, label: 'Bebo IA', path: '/admin/bebo' }] : []),
        { icon: Home, label: 'Inicio', path: '/home', dataTour: 'nav-inicio' },
        ...(panelItem ? [panelItem] : []),
        ...(isAdmin
          ? [
              { icon: CalendarRange, label: 'Media planning', path: '/admin/media-planning' },
              { icon: CalendarDays, label: 'Calendario de promos', path: '/admin/promo-calendar' },
              { icon: FileText, label: 'Reportes', path: '/admin/reportes' },
            ]
          : []),
        { icon: Rocket, label: 'Optimizaciones', path: '/optimizador', dataTour: 'nav-optimizaciones' },
        { icon: Target, label: 'Objetivos', path: '/objetivos', dataTour: 'nav-objetivos' },
        ...(showCampaignsModule ? [{ icon: Zap, label: 'Campañas', path: '/campaigns' }] : []),
        { icon: Megaphone, label: 'Anuncios', path: '/ads-age', dataTour: 'nav-anuncios' },
        { icon: BarChart2, label: 'Analytics', path: '/analytics20', dataTour: 'nav-analytics' },
        { icon: User, label: 'Mi perfil', path: '/profile', disabled: false }
      ];
    },
    [showCampaignsModule, panelItem, isAdmin, isGrowthPartner]
  );

  const mobileMenuItemsWithLock = useMemo(
    () => mobileMenuBase.map((item) => ({ ...item, disabled: Boolean(item.disabled) || chromeLocked })),
    [mobileMenuBase, chromeLocked]
  );

  if (screenSize.isMobile || screenSize.isTablet) {
    return (
      <>
        {/* Hamburger button, top-left */}
        <button
          type="button"
          aria-label="Abrir menú"
          onClick={() => setMobileMenuOpen(true)}
          className="fixed top-3 left-3 z-[9998] w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 text-white flex items-center justify-center active:scale-95 transition-transform"
        >
          <Menu size={22} />
        </button>

        {/* Slide-in drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[10000]">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="absolute left-0 top-0 h-full w-72 max-w-[82%] bg-[#111317] border-r border-white/10 shadow-2xl flex flex-col p-3 overflow-y-auto">
              <div className="flex items-center justify-between px-2 py-2 mb-2">
                <svg width="28" height="28" viewBox="0 0 174 175" fill="none">
                  <path d="M134.26 37.04C134.26 90.99 90.37 134.89 36.41 134.89V174.46C112.18 174.46 173.82 112.82 173.82 37.05H134.26V37.04Z" fill="#2f3df7" />
                  <path d="M19.63 39.26C30.4713 39.26 39.26 30.4713 39.26 19.63C39.26 8.78865 30.4713 0 19.63 0C8.78865 0 0 8.78865 0 19.63C0 30.4713 8.78865 39.26 19.63 39.26Z" fill="#2f3df7" />
                  <path d="M86.12 39.26C96.9613 39.26 105.75 30.4713 105.75 19.63C105.75 8.78865 96.9613 0 86.12 0C75.2787 0 66.42 8.78865 66.49 19.63C66.49 30.4713 75.2787 39.26 86.12 39.26Z" fill="#2f3df7" />
                </svg>
                <button
                  type="button"
                  aria-label="Cerrar menú"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-9 h-9 rounded-lg text-white/70 hover:bg-white/10 flex items-center justify-center"
                >
                  <X size={20} />
                </button>
              </div>
              {mobileMenuItemsWithLock.map(item => {
                const isActive = item.path === '/profile'
                  ? location.pathname.startsWith('/profile')
                  : location.pathname === item.path;
                return (
                  <button
                    key={item.path || item.label}
                    type="button"
                    disabled={item.disabled}
                    onClick={(e) => {
                      if (item.onClick) {
                        e.preventDefault();
                        item.onClick();
                      } else {
                        handleNavigation(item.path, item.disabled, item.label, e);
                      }
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 w-full px-3 py-3 rounded-xl text-left transition-colors ${
                      item.disabled
                        ? 'opacity-50 cursor-not-allowed pointer-events-none text-white/50'
                        : isActive
                          ? 'bg-white/15 text-white'
                          : 'text-white/85 hover:bg-white/10'
                    }`}
                  >
                    <item.icon size={20} className="flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="mt-auto flex items-center gap-3 w-full px-3 py-3 rounded-xl text-left text-white/85 hover:bg-white/10 border-t border-white/10"
              >
                <LogOut size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Cerrar sesión</span>
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Admin desktop: narrow ClickUp-style rail (icon + label stacked).
  if (screenSize.isDesktop && isAdmin) {
    const railItems = [
      { icon: Brain, label: 'Bebo IA', path: '/admin/bebo', bebo: true },
      { icon: LayoutDashboard, label: 'Panel', path: panelItem?.path || '/admin/panel' },
      { icon: FileText, label: 'Reportes', path: '/admin/reportes' },
    ];
    const otrasItems = [
      { icon: Home, label: 'Inicio', path: '/home' },
      { icon: CalendarRange, label: 'Media planning', path: '/admin/media-planning' },
      { icon: CalendarDays, label: 'Calendario de promos', path: '/admin/promo-calendar' },
      { icon: Rocket, label: 'Optimizaciones', path: '/optimizador' },
      { icon: Target, label: 'Objetivos', path: '/objetivos' },
      ...(showCampaignsModule ? [{ icon: Zap, label: 'Campañas', path: '/campaigns' }] : []),
      { icon: Megaphone, label: 'Anuncios', path: '/ads-age' },
      { icon: BarChart2, label: 'Analytics', path: '/analytics20' },
      { icon: Plug, label: 'Conexiones', onClick: () => openConnectionsModal?.() },
      { icon: HelpCircle, label: 'Soporte', onClick: () => openHelpCenter() },
      { icon: CreditCard, label: 'Suscripción', onClick: () => openPlansModal?.() },
      { icon: Settings, label: 'Configuración', path: '/profile' },
    ];
    const runItem = (it, e) => {
      if (it.onClick) {
        e?.preventDefault();
        it.onClick();
      } else {
        handleNavigation(it.path, chromeLocked, it.label, e);
      }
    };

    return (
      <aside className="bebo-rail">
        <button
          type="button"
          className="bebo-rail-logo"
          aria-label="Inicio"
          onClick={() => {
            if (!chromeLocked) navigate('/home');
          }}
        >
          <svg width="30" height="30" viewBox="0 0 174 175" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M134.26 37.04C134.26 90.99 90.37 134.89 36.41 134.89V174.46C112.18 174.46 173.82 112.82 173.82 37.05H134.26V37.04Z" fill="#0F31F7" />
            <path d="M19.63 39.26C30.4713 39.26 39.26 30.4713 39.26 19.63C39.26 8.78865 30.4713 0 19.63 0C8.78865 0 0 8.78865 0 19.63C0 30.4713 8.78865 39.26 19.63 39.26Z" fill="#0F31F7" />
            <path d="M86.12 39.26C96.9613 39.26 105.75 30.4713 105.75 19.63C105.75 8.78865 96.9613 0 86.12 0C75.2787 0 66.42 8.78865 66.49 19.63C66.49 30.4713 75.2787 39.26 86.12 39.26Z" fill="#0F31F7" />
          </svg>
        </button>

        <nav className="bebo-rail-nav">
          {railItems.map((it) => {
            const active =
              location.pathname === it.path || location.pathname.startsWith(it.path);
            return (
              <button
                key={it.path}
                type="button"
                disabled={chromeLocked}
                onClick={(e) => runItem(it, e)}
                className={`bebo-rail-item ${active ? 'active' : ''} ${it.bebo ? 'bebo' : ''}`}
              >
                <span className="bebo-rail-ic">
                  <it.icon size={20} />
                </span>
                <span className="bebo-rail-lbl">{it.label}</span>
              </button>
            );
          })}

          <div className="bebo-rail-more">
            <button
              type="button"
              className={`bebo-rail-item ${showMore ? 'active' : ''}`}
              onClick={() => setShowMore((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={showMore}
            >
              <span className="bebo-rail-ic">
                <NineDots />
              </span>
              <span className="bebo-rail-lbl">Más</span>
            </button>
            {showMore && (
              <>
                <div className="bebo-rail-backdrop" onClick={() => setShowMore(false)} />
                <div className="bebo-rail-flyout" role="menu">
                  {otrasItems.map((it) => (
                    <button
                      key={it.path || it.label}
                      type="button"
                      className="bebo-rail-flyout-item"
                      onClick={(e) => {
                        runItem(it, e);
                        setShowMore(false);
                      }}
                    >
                      <it.icon size={17} />
                      <span>{it.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </nav>

        <div className="bebo-rail-profile" ref={profileMenuRef}>
          <button
            type="button"
            className={`bebo-rail-item ${showProfileMenu ? 'active' : ''}`}
            aria-label="Cuenta"
            aria-haspopup="menu"
            aria-expanded={showProfileMenu}
            onClick={() => setShowProfileMenu((v) => !v)}
          >
            <span className="bebo-rail-avatar">
              {auth.user?.profile_image ? (
                <img src={auth.user.profile_image} alt="" />
              ) : (
                auth.user?.first_name?.[0]?.toUpperCase() || 'U'
              )}
            </span>
            <span className="bebo-rail-lbl">Perfil</span>
          </button>
          {showProfileMenu && (
            <div className="bebo-rail-flyout" role="menu">
              <button
                type="button"
                className="bebo-rail-flyout-item"
                onClick={() => {
                  navigate('/profile');
                  setShowProfileMenu(false);
                }}
              >
                <User size={17} />
                <span>Mi perfil</span>
              </button>
              <button type="button" className="bebo-rail-flyout-item" onClick={handleLogout}>
                <LogOut size={17} />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </aside>
    );
  }

  return (
    <>
      {/* Desktop Vertical Sidebar */}
      {screenSize.isDesktop && (
        <aside 
          ref={sidebarRef}
          className="backdrop-blur-xl bg-[#111317] border-r border-white/10 shadow-2xl fixed left-0 z-[99999] w-[240px]"
          style={{ 
            zIndex: 99999,
            top: 0,
            left: 0,
            height: '100vh',
            margin: 0,
            padding: 0,
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div className="flex flex-col h-full" style={{ margin: 0, padding: 0 }}>
            {/* Logo */}
            <div className="flex items-center justify-start px-5 pt-7 pb-3">
              <div
                role="button"
                tabIndex={chromeLocked ? -1 : 0}
                aria-disabled={chromeLocked}
                onClick={() => {
                  if (chromeLocked) return;
                  navigate('/home');
                }}
                onKeyDown={(e) => {
                  if (chromeLocked) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/home');
                  }
                }}
                className={`transition-transform duration-300 ease-out ${
                  chromeLocked
                    ? 'opacity-40 cursor-not-allowed pointer-events-none'
                    : 'cursor-pointer hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md'
                }`}
              >
                <svg width="32" height="32" viewBox="0 0 174 175" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M134.26 37.04C134.26 90.99 90.37 134.89 36.41 134.89V174.46C112.18 174.46 173.82 112.82 173.82 37.05H134.26V37.04Z" fill="#0F31F7"/>
                  <path d="M19.63 39.26C30.4713 39.26 39.26 30.4713 39.26 19.63C39.26 8.78865 30.4713 0 19.63 0C8.78865 0 0 8.78865 0 19.63C0 30.4713 8.78865 39.26 19.63 39.26Z" fill="#0F31F7"/>
                  <path d="M86.12 39.26C96.9613 39.26 105.75 30.4713 105.75 19.63C105.75 8.78865 96.9613 0 86.12 0C75.2787 0 66.42 8.78865 66.49 19.63C66.49 30.4713 75.2787 39.26 86.12 39.26Z" fill="#0F31F7"/>
                </svg>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-6 px-1">
              {menuItemsWithLock.map(item => {
                if (item.section) {
                  if (item.collapsible) {
                    return (
                      <button
                        key={`section-${item.section}`}
                        type="button"
                        onClick={() => setShowOtras((v) => !v)}
                        className="flex items-center gap-2 w-full px-3 pt-5 pb-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35 hover:text-white/60 transition-colors"
                      >
                        {item.section}
                        <ChevronDown
                          size={12}
                          className={`ml-auto transition-transform duration-200 ${showOtras ? '' : '-rotate-90'}`}
                        />
                      </button>
                    );
                  }
                  return (
                    <div key={`section-${item.section}`} className="px-3 pt-5 pb-1.5 first:pt-2">
                      <span className="block text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">
                        {item.section}
                      </span>
                    </div>
                  );
                }
                if (item.group === 'otras' && !showOtras) {
                  return null;
                }
                const hasSubmenu = Array.isArray(item.submenu);
                const isActive = hasSubmenu
                  ? item.submenu.some(sub => location.pathname === sub.path)
                  : location.pathname === item.path;
                const isOptimizerItem = item.path === '/optimizador';

                // Item Optimizaciones bloqueado: mantiene estilo bloqueado pero navega al optimizador
                if (isOptimizerItem && isOptimizerLocked && !chromeLocked) {
                  return (
                    <div key={item.path || item.label} className="relative group mb-2">
                      <a
                        href={item.path}
                        onClick={(e) => handleNavigation(item.path, item.disabled, item.label, e)}
                        onMouseDown={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            handleNavigation(item.path, item.disabled, item.label, e);
                          }
                        }}
                        className={`flex flex-row items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-300 ease-out relative cursor-pointer ${
                          isActive
                            ? 'bg-amber-500/25 border-2 border-amber-400/60 shadow-md shadow-amber-500/20'
                            : 'bg-amber-900/35 border-2 border-amber-500/50 hover:bg-amber-500/30 hover:border-amber-400/60'
                        }`}
                        {...(item.dataTour && { 'data-tour': item.dataTour })}
                      >
                        <div className="flex flex-row items-center gap-3">
                          <Lock size={20} className="text-amber-300 flex-shrink-0" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-left text-amber-200/95">
                              {item.label}
                            </span>
                            <span className="text-[8px] font-medium text-amber-300/80 uppercase tracking-wide">
                              Bloqueado
                            </span>
                          </div>
                        </div>
                      </a>
                    </div>
                  );
                }

                if (hasSubmenu) {
                  if (item.disabled) {
                    return (
                      <div key={item.path || item.label} className="relative group mb-2">
                        <div
                          className="flex flex-row items-center gap-3 w-full px-3 py-2.5 rounded-xl opacity-50 cursor-not-allowed pointer-events-none border border-transparent"
                          aria-disabled="true"
                          title={CHROME_LOCK_TOOLTIP}
                        >
                          <item.icon size={20} className="flex-shrink-0 text-white/50" />
                          <span className="text-sm font-medium text-left text-white/50">
                            {item.label}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  const submenuVisible = openSubmenuLabel === item.label;
                  return (
                    <div
                      key={item.path || item.label}
                      className="relative group mb-2"
                      onMouseEnter={() => setOpenSubmenuLabel(item.label)}
                      onMouseLeave={() => setOpenSubmenuLabel(null)}
                      onFocus={() => setOpenSubmenuLabel(item.label)}
                      onBlur={(event) => {
                        if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
                          return;
                        }
                        setOpenSubmenuLabel(null);
                      }}
                    >
                      <a
                        href={item.path}
                        aria-haspopup="menu"
                      aria-expanded={submenuVisible}
                        {...(item.dataTour && { 'data-tour': item.dataTour })}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigation(item.path, item.disabled, item.label, e);
                        }}
                        onMouseDown={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            handleNavigation(item.path, item.disabled, item.label, e);
                          }
                        }}
                        className={`
                          flex flex-row items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-300 ease-out relative
                          ${isActive
                            ? 'bg-white/20 backdrop-blur-md border border-white/30 shadow-lg shadow-blue-500/20'
                            : 'hover:bg-white/10 hover:backdrop-blur-sm border border-transparent hover:border-white/20'
                          }
                        `}
                        style={isActive ? {
                          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.15)'
                        } : {}}
                      >
                        <item.icon
                          size={20}
                          className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-white/95'}`}
                        />
                        <span className={`text-sm font-medium text-left ${isActive ? 'text-white' : 'text-white/90'}`}>
                          {item.label}
                        </span>
                      </a>

                      <div
                        className={`
                          absolute left-full top-2 translate-y-0 w-56 transition-all duration-200 rounded-2xl border border-white/15 bg-slate-900/95 p-3 shadow-2xl backdrop-blur-xl text-white text-sm
                          ${submenuVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
                        `}
                        style={{ left: 'calc(100% + 2px)' }}
                        tabIndex={-1}
                      >
                        <div className="flex flex-col gap-1">
                          {item.submenu.map(sub => (
                            <button
                              key={sub.path}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigation(sub.path, item.disabled, sub.label, e);
                              }}
                              className="flex flex-col text-left rounded-xl px-3 py-2 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                              <span className="font-semibold text-white">{sub.label}</span>
                              <span className="text-[11px] text-white/60">{sub.description}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={item.path || item.label} className="relative group mb-2">
                    <a
                      href={item.disabled ? undefined : item.path}
                      {...(item.dataTour && { 'data-tour': item.dataTour })}
                      onClick={(e) => {
                        if (item.onClick) {
                          e.preventDefault();
                          item.onClick();
                        } else {
                          handleNavigation(item.path, item.disabled, item.label, e);
                        }
                      }}
                      onMouseDown={(e) => {
                        if (e.button === 1 && !item.disabled) {
                          e.preventDefault();
                          handleNavigation(item.path, item.disabled, item.label, e);
                        }
                      }}
                      className={`
                        flex flex-row items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-300 ease-out relative
                        ${item.disabled
                          ? 'opacity-50 cursor-not-allowed pointer-events-none'
                          : isActive
                          ? 'bg-white/20 backdrop-blur-md border border-white/30 shadow-lg shadow-blue-500/20'
                          : 'hover:bg-white/10 hover:backdrop-blur-sm border border-transparent hover:border-white/20'
                        }
                      `}
                      style={isActive ? {
                        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.15)'
                      } : {}}
                    >
                      <item.icon
                        size={20}
                        className={`flex-shrink-0 ${
                          item.disabled
                            ? 'text-white/50'
                            : isActive
                            ? 'text-white'
                            : 'text-white/95'
                        }`}
                      />
                      <span className={`text-sm font-medium text-left ${
                        item.disabled
                          ? 'text-white/50'
                          : isActive
                          ? 'text-white'
                          : 'text-white/90'
                      }`}>
                        {item.label}
                      </span>
                    </a>
                  </div>
                );
              })}
            </nav>

            {/* Profile Menu */}
            <div className="relative px-2 py-3" ref={profileMenuRef}>
              <button
                type="button"
                data-testid="sidebar-profile-menu-button"
                aria-haspopup="menu"
                aria-expanded={showProfileMenu}
                aria-label="Menú de cuenta"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex flex-row items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-white/10 hover:backdrop-blur-sm transition-all duration-300 border border-transparent hover:border-white/20"
              >
                <div className="relative flex-shrink-0">
                  <div 
                    className="h-10 w-10 rounded-full backdrop-blur-md bg-white/15 flex items-center justify-center border-2 border-white/30 transition-all duration-300 hover:scale-110 hover:border-white/50 relative overflow-hidden"
                    style={{
                      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    {/* Efecto de brillo superior */}
                    <div 
                      className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent rounded-t-full pointer-events-none"
                    />
                    {auth.user?.profile_image ? (
                      <img 
                        key={`profile-image-${auth.user.profile_image}`} 
                        src={auth.user.profile_image} 
                        alt="Profile" 
                        className="rounded-full h-10 w-10 flex-shrink-0 object-cover relative z-10" 
                      />
                    ) : (
                      <span className="text-sm font-bold text-white flex-shrink-0 relative z-10">
                        {auth.user?.first_name?.[0]?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-sm font-medium text-left text-white/90">
                  {auth.user?.name || auth.user?.first_name || 'Usuario'}
                </span>
              </button>

              {showProfileMenu && (
                <div
                  className="absolute bottom-full left-0 mb-2 ml-2 w-64 backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 rounded-lg shadow-2xl overflow-hidden border border-white/20 z-50"
                  style={{
                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px rgba(59, 130, 246, 0.1)'
                  }}
                >
                  {profileItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => { 
                        item.onClick(); 
                        setShowProfileMenu(false); 
                      }}
                      className="flex items-center w-full px-4 py-3 text-white/90 hover:bg-white/10 hover:text-white transition-all duration-300 border-b border-white/5 last:border-b-0"
                    >
                      <item.icon size={16} className="mr-3 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Mobile Footer Menu */}
      {!screenSize.isDesktop && showMobileFooterMenu && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
          {/* Brand Selector Section */}
          {auth.user?.brands?.find(b => b.id === auth.user?.active_brand_id) && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Connection Badges */}
                <CompactConnectionBadge
                  connected={googleConnected}
                  icon={GoogleIcon}
                  label="Google Ads"
                  disabled={chromeLocked}
                  disabledReason={chromeLocked ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={googleConnected ? openConnectionsModal : () => connectGoogleAds(false)}
                />
                <CompactConnectionBadge
                  connected={metaConnected}
                  icon={MetaIcon}
                  label="Meta Ads"
                  disabled={chromeLocked}
                  disabledReason={chromeLocked ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={metaConnected ? openConnectionsModal : () => startMetaOAuth(false)}
                />
              </div>
              {/* Brand Selector Button */}
              <button
                type="button"
                disabled={chromeLocked}
                aria-disabled={chromeLocked}
                onClick={() => {
                  if (chromeLocked) return;
                  setIsBrandSelectorOpen(true);
                }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border border-gray-300 text-gray-900 text-xs font-medium transition-all touch-manipulation flex-shrink-0 ${
                  chromeLocked
                    ? 'opacity-50 cursor-not-allowed pointer-events-none bg-gray-100'
                    : 'bg-white/10 hover:bg-gray-100 active:scale-95'
                }`}
                title={chromeLocked ? CHROME_LOCK_TOOLTIP : 'Cambiar marca'}
              >
                <span className="truncate max-w-[60px]">{auth.user?.brands?.find(b => b.id === auth.user?.active_brand_id)?.name || 'Marca'}</span>
                <ChevronDown size={12} className="flex-shrink-0" />
              </button>
            </div>
          )}
          <div className="flex justify-around items-center py-2">
            {mobileMenuItemsWithLock.map(item => {
              const mobileLabel = item.label;
              const isActive = item.path === '/profile'
                ? location.pathname.startsWith('/profile')
                : location.pathname === item.path;
              const baseClassName = `flex flex-col items-center p-2 rounded-lg transition-colors min-w-0 flex-1 ${
                item.disabled
                  ? 'opacity-50 cursor-not-allowed pointer-events-none'
                  : isActive
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-100'
              }`;
              const content = (
                <>
                  <item.icon size={20} />
                  <span className="text-xs font-medium mt-1 truncate w-full text-center">
                    {mobileLabel}
                  </span>
                </>
              );
              if (item.disabled || item.onClick) {
                return (
                  <button
                    key={item.path || item.label}
                    type="button"
                    disabled={item.disabled}
                    aria-label={
                      chromeLocked && item.disabled
                        ? `${mobileLabel}. ${CHROME_LOCK_TOOLTIP}`
                        : mobileLabel
                    }
                    title={chromeLocked && item.disabled ? CHROME_LOCK_TOOLTIP : undefined}
                    onClick={(e) => {
                      if (item.onClick) item.onClick();
                      else handleNavigation(item.path, item.disabled, item.label, e);
                    }}
                    className={baseClassName}
                  >
                    {content}
                  </button>
                );
              }
              return (
                <a
                  key={item.path || item.label}
                  href={item.path}
                  aria-label={
                    chromeLocked && item.disabled
                      ? `${mobileLabel}. ${CHROME_LOCK_TOOLTIP}`
                      : undefined
                  }
                  title={chromeLocked && item.disabled ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={(e) => handleNavigation(item.path, item.disabled, item.label, e)}
                  onMouseDown={(e) => {
                    if (e.button === 1) {
                      e.preventDefault();
                      handleNavigation(item.path, item.disabled, item.label, e);
                    }
                  }}
                  className={baseClassName}
                >
                  {content}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Brand Selector Sheet */}
      {!screenSize.isDesktop && (
        <MobileBrandSelectorSheet
          isOpen={isBrandSelectorOpen}
          onClose={() => setIsBrandSelectorOpen(false)}
        />
      )}
    </>
  );
};

Sidebar.propTypes = {
  onExpandedChange: PropTypes.func,
  onNavigate: PropTypes.func,
  showMobileFooterMenu: PropTypes.bool,
  onSidebarStateChange: PropTypes.func
};

export default Sidebar;